import { Box, Flex, Input, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { fmtIntegerInput, parseIntegerInput } from './calc/formatters';
import { resolveMonthlyFeePct } from './calc/xirrFee';
import { PRO_RATA_REV_SHARE_TOOLTIP } from './calc/resolvePlanFunding';
import { isRevSharePctConfigured } from './calc/resolveProRataRevShare';
import { isPlanMonthOutsideCommitment } from './calc/resolveFundingCommitment';
import {
  formatDealIrrMonthInputValue,
  updateDealIrrTableFirstMarketingMonth,
} from './calc/dealIrrTableEdits';
import { resolveFirstMarketingMonth } from './calc/resolveDealIrrModelInputs';
import {
  applyPastedMarketingSpend,
  parseMarketingSpendPaste,
} from './calc/pasteMarketingSpend';
import MarketingRoasVectorTooltip from './MarketingRoasVectorPopover';
import { HelperTooltip } from 'components/Tooltip';

export function marketingPlanScrollMax(scrollWidth, clientWidth) {
  return Math.max(0, Number(scrollWidth) - Number(clientWidth));
}

function MarketingPlanMonthsRow({
  state,
  setState,
  planMonths,
  spendRoasVectors,
  useHillRoas,
  showMonthlyIrrRow = false,
  editableFirstMonth = false,
  allowPasteSpend = false,
}) {
  const scrollRef = useRef(null);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [scrollMax, setScrollMax] = useState(0);

  const totalMarketingSpend = useMemo(
    () => (state.marketing || []).reduce((sum, value) => sum + (Number(value) || 0), 0),
    [state.marketing]
  );
  const totalPlanDraw = useMemo(
    () =>
      (state.marketing || []).reduce(
        (sum, _, index) =>
          sum +
          (isPlanMonthOutsideCommitment(state, index)
            ? 0
            : Number(state.planDrawAmount?.[index]) || 0),
        0
      ),
    [state]
  );

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return undefined;
    }
    const syncScrollMetrics = () => {
      setScrollMax(marketingPlanScrollMax(el.scrollWidth, el.clientWidth));
      setScrollLeft(el.scrollLeft);
    };
    syncScrollMetrics();
    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(syncScrollMetrics)
        : null;
    resizeObserver?.observe(el);
    window.addEventListener('resize', syncScrollMetrics);
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', syncScrollMetrics);
    };
  }, [planMonths.length, state.marketingPlanManualMode]);

  const onSliderChange = (value) => {
    const el = scrollRef.current;
    if (el) {
      el.scrollLeft = value;
    }
    setScrollLeft(value);
  };

  return (
    <Box>
      <Flex align="stretch" gap={4}>
        <Box flex={1} minW={0}>
          <Box
            ref={scrollRef}
            className="marketing-plan-months-scroll"
            data-testid="marketing-plan-months-scroll"
            onScroll={(e) => setScrollLeft(e.currentTarget.scrollLeft)}
          >
            <Flex className="marketing-plan-months-row">
              {planMonths.map((monthLabel, index) => {
                const outsideCommitment = isPlanMonthOutsideCommitment(
                  state,
                  index
                );
                const spend = Number(state.marketing[index]) || 0;
                const roasVector = useHillRoas
                  ? spendRoasVectors?.[index]
                  : null;
                return (
                  <Box
                    key={`plan-month-${index}`}
                    className={`marketing-plan-month-col${
                      outsideCommitment
                        ? ' marketing-plan-month-col--outside-commitment'
                        : ''
                    }${
                      state.marketingPlanManualMode
                        ? ' marketing-plan-month-col--manual'
                        : ''
                    }`}
                    data-testid={`marketing-plan-month-${index}`}
                  >
                    {editableFirstMonth && index === 0 ? (
                      <Input
                        aria-label="First marketing month"
                        className="marketing-plan-month-input"
                        size="xs"
                        type="month"
                        value={formatDealIrrMonthInputValue(
                          resolveFirstMarketingMonth(state)
                        )}
                        onChange={(event) =>
                          updateDealIrrTableFirstMarketingMonth(
                            setState,
                            event.target.value
                          )
                        }
                      />
                    ) : (
                      <Text className="marketing-plan-month-name">{monthLabel}</Text>
                    )}
                    <MarketingRoasVectorTooltip
                      vectorPct={roasVector}
                      spend={spend}
                    >
                      <Input
                        aria-label={`Marketing spend ${monthLabel}`}
                        type="text"
                        inputMode="numeric"
                        size="sm"
                        textAlign="right"
                        value={fmtIntegerInput(state.marketing[index])}
                        onPaste={(event) => {
                          if (!allowPasteSpend) {
                            return;
                          }
                          const pasted = parseMarketingSpendPaste(
                            event.clipboardData?.getData('text') ?? ''
                          );
                          if (pasted.length <= 1) {
                            return;
                          }
                          event.preventDefault();
                          setState((currentState) =>
                            applyPastedMarketingSpend(
                              currentState,
                              pasted,
                              index
                            )
                          );
                        }}
                        onChange={(e) => {
                          const val = parseIntegerInput(e.target.value);
                          setState((s) => {
                            const marketing = [...s.marketing];
                            marketing[index] = val;
                            const next = { ...s, marketing };
                            if (s.marketingPlanManualMode) {
                              const fundingPct = [...s.fundingPct];
                              const planDrawAmount = [
                                ...(s.planDrawAmount ?? []),
                              ];
                              while (planDrawAmount.length <= index) {
                                planDrawAmount.push(null);
                              }
                              const pct = Number(fundingPct[index]) || 0;
                              planDrawAmount[index] =
                                val > 0 && pct > 0
                                  ? Math.min(Math.round((val * pct) / 100), val)
                                  : null;
                              next.fundingPct = fundingPct;
                              next.planDrawAmount = planDrawAmount;
                            }
                            return next;
                          });
                        }}
                      />
                    </MarketingRoasVectorTooltip>
                    {state.marketingPlanManualMode ? (
                      <Box className="marketing-plan-month-manual">
                        {showMonthlyIrrRow ? (
                          <>
                            <Text className="marketing-plan-month-field-label">
                              IRR % / month
                            </Text>
                            <Input
                              aria-label={`IRR % / month ${monthLabel}`}
                              type="number"
                              size="sm"
                              min={0}
                              step={0.01}
                              textAlign="right"
                              value={
                                state.monthlyIrrPct?.[index] ??
                                resolveMonthlyFeePct(state.feeType, state.feePct)
                              }
                              onChange={(e) => {
                                const val = Number(e.target.value) || 0;
                                setState((s) => {
                                  const monthlyIrrPct = [...(s.monthlyIrrPct || [])];
                                  while (monthlyIrrPct.length <= index) {
                                    monthlyIrrPct.push(
                                      resolveMonthlyFeePct(s.feeType, s.feePct)
                                    );
                                  }
                                  monthlyIrrPct[index] = val;
                                  return { ...s, monthlyIrrPct };
                                });
                              }}
                            />
                          </>
                        ) : null}
                        <Text className="marketing-plan-month-field-label">
                          Max Funding %
                        </Text>
                        <Input
                          aria-label={`Max Funding % ${monthLabel}`}
                          type="number"
                          size="sm"
                          textAlign="right"
                          value={state.fundingPct[index]}
                          isDisabled={outsideCommitment}
                          onChange={(e) => {
                            const val = Number(e.target.value) || 0;
                            setState((s) => {
                              const fundingPct = [...s.fundingPct];
                              fundingPct[index] = val;
                              const planDrawAmount = [
                                ...(s.planDrawAmount ?? []),
                              ];
                              while (planDrawAmount.length <= index) {
                                planDrawAmount.push(null);
                              }
                              const marketing = Number(s.marketing[index]) || 0;
                              planDrawAmount[index] =
                                marketing > 0 && val > 0
                                  ? Math.min(
                                      Math.round((marketing * val) / 100),
                                      marketing
                                    )
                                  : null;
                              return { ...s, fundingPct, planDrawAmount };
                            });
                          }}
                        />
                        <Text className="marketing-plan-month-field-label">
                          Funding ($)
                        </Text>
                        <Input
                          aria-label={`Funding ${monthLabel}`}
                          type="text"
                          inputMode="numeric"
                          size="sm"
                          textAlign="right"
                          placeholder="0"
                          value={
                            state.planDrawAmount?.[index] != null
                              ? fmtIntegerInput(state.planDrawAmount[index])
                              : ''
                          }
                          isDisabled={outsideCommitment}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/,/g, '').trim();
                            setState((s) => {
                              const planDrawAmount = [
                                ...(s.planDrawAmount ?? []),
                              ];
                              while (planDrawAmount.length <= index) {
                                planDrawAmount.push(null);
                              }
                              const drawAmount =
                                raw === ''
                                  ? null
                                  : Math.max(0, parseIntegerInput(raw));
                              planDrawAmount[index] = drawAmount;
                              const fundingPct = [...s.fundingPct];
                              const marketing = Number(s.marketing[index]) || 0;
                              if (drawAmount == null || marketing <= 0) {
                                fundingPct[index] = 0;
                              } else {
                                fundingPct[index] = Math.min(
                                  100,
                                  Math.round((drawAmount * 100) / marketing)
                                );
                              }
                              return { ...s, planDrawAmount, fundingPct };
                            });
                          }}
                        />
                        <Flex align="center" gap={0}>
                          <Text className="marketing-plan-month-field-label">
                            Max Revenue share %
                          </Text>
                          <HelperTooltip content={PRO_RATA_REV_SHARE_TOOLTIP} />
                        </Flex>
                        <Input
                          aria-label={`Max Revenue share % ${monthLabel}`}
                          type="number"
                          size="sm"
                          min={0}
                          step={0.01}
                          textAlign="right"
                          placeholder="Pro-rata"
                          value={
                            isRevSharePctConfigured(state.revSharePct?.[index])
                              ? state.revSharePct[index]
                              : ''
                          }
                          isDisabled={outsideCommitment}
                          onChange={(e) => {
                            const raw = e.target.value.trim();
                            setState((s) => {
                              const revSharePct = [...s.revSharePct];
                              revSharePct[index] =
                                raw === '' ? null : Number(raw) || 0;
                              return { ...s, revSharePct };
                            });
                          }}
                        />
                        <Text className="marketing-plan-month-field-label">
                          Max exposure ($)
                        </Text>
                        <Input
                          aria-label={`Max exposure ${monthLabel}`}
                          type="text"
                          inputMode="numeric"
                          size="sm"
                          textAlign="right"
                          placeholder={
                            state.maxExposure != null
                              ? fmtIntegerInput(state.maxExposure)
                              : 'Default'
                          }
                          value={
                            state.planMaxExposure?.[index] != null
                              ? fmtIntegerInput(state.planMaxExposure[index])
                              : ''
                          }
                          isDisabled={outsideCommitment}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/,/g, '').trim();
                            setState((s) => {
                              const planMaxExposure = [
                                ...(s.planMaxExposure ?? []),
                              ];
                              while (planMaxExposure.length <= index) {
                                planMaxExposure.push(null);
                              }
                              planMaxExposure[index] =
                                raw === ''
                                  ? null
                                  : Math.max(0, parseIntegerInput(raw));
                              return { ...s, planMaxExposure };
                            });
                          }}
                        />
                      </Box>
                    ) : null}
                  </Box>
                );
              })}
            </Flex>
          </Box>
          <input
            type="range"
            aria-label="Scroll marketing months"
            className="marketing-plan-months-slider"
            min={0}
            max={scrollMax > 0 ? scrollMax : 1}
            value={Math.min(scrollLeft, scrollMax > 0 ? scrollMax : 1)}
            disabled={scrollMax <= 0}
            onChange={(e) => onSliderChange(Number(e.target.value))}
          />
        </Box>
        <Box className="marketing-plan-months-total" data-testid="marketing-plan-months-total">
          <Text className="marketing-plan-month-name">Total</Text>
          <Text fontWeight="semibold" textAlign="right">
            ${totalMarketingSpend.toLocaleString()}
          </Text>
          {state.marketingPlanManualMode ? (
            <>
              <Text className="marketing-plan-month-field-label" mt={3}>
                Funding ($)
              </Text>
              <Text fontWeight="semibold" textAlign="right">
                ${totalPlanDraw.toLocaleString()}
              </Text>
            </>
          ) : null}
        </Box>
      </Flex>
    </Box>
  );
}

MarketingPlanMonthsRow.propTypes = {
  state: PropTypes.object.isRequired,
  setState: PropTypes.func.isRequired,
  planMonths: PropTypes.arrayOf(PropTypes.string).isRequired,
  spendRoasVectors: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
  useHillRoas: PropTypes.bool,
  showMonthlyIrrRow: PropTypes.bool,
  editableFirstMonth: PropTypes.bool,
  allowPasteSpend: PropTypes.bool,
};

export default MarketingPlanMonthsRow;
